<template>
  <section class="notification-center">
    <header class="center-header">
      <div>
        <div class="eyebrow">MESSAGE CENTER</div>
        <div class="title-line">
          <h1>消息中心</h1>
          <span class="connection-state" :class="`is-${socketState}`" aria-live="polite">
            <i></i>{{ socketStateLabel }}
          </span>
        </div>
        <p>来自各业务服务器的通知会在这里汇入同一条时间流。</p>
      </div>
      <div class="header-actions">
        <div class="flow-summary">
          <span>当前结果</span>
          <strong>{{ events.length }}</strong>
          <small>条</small>
        </div>
        <el-button icon="el-icon-back" @click="goHome">返回首页</el-button>
        <el-button :icon="isFullscreen ? 'el-icon-close' : 'el-icon-full-screen'" @click="toggleFullscreen">
          {{ isFullscreen ? '退出全屏' : '进入全屏' }}
        </el-button>
      </div>
    </header>

    <div v-if="authRequired" class="auth-state">
      <div class="auth-mark"><i class="el-icon-lock"></i></div>
      <h2>需要重新登录</h2>
      <p>{{ authMessage }}</p>
      <el-button type="primary" @click="reauthenticate">返回登录</el-button>
    </div>

    <template v-else>
      <div class="filter-bar">
        <div class="filter-field filter-search">
          <label for="notification-search">搜索内容</label>
          <el-input
            id="notification-search"
            v-model="filters.keyword"
            clearable
            prefix-icon="el-icon-search"
            placeholder="输入订单、项目或错误关键字"
            @input="queueRefresh"
            @clear="refreshFlow"
          />
        </div>
        <div class="filter-field">
          <label>群组</label>
          <el-select v-model="filters.room" clearable filterable placeholder="全部群组" @change="refreshFlow">
            <el-option v-for="room in filterOptions.rooms" :key="room" :label="room" :value="room" />
          </el-select>
        </div>
        <div class="filter-field">
          <label>级别</label>
          <el-select v-model="filters.severity" clearable placeholder="全部级别" @change="refreshFlow">
            <el-option label="普通（其他消息）" value="info" />
            <el-option label="成功（成功 / 完成）" value="success" />
            <el-option label="注意（警告 / 待处理）" value="warning" />
            <el-option label="异常（失败 / 错误）" value="error" />
          </el-select>
        </div>
        <el-button class="reset-button" :disabled="!hasActiveFilters" @click="resetFilters">清除筛选</el-button>
      </div>

      <div v-if="newEventCount" class="new-event-strip" role="status" @click="showNewest">
        <span>{{ newEventCount }} 条新消息已到达</span>
        <button type="button">回到顶部 <i class="el-icon-top"></i></button>
      </div>

      <div ref="flow" class="flow" @scroll="handleScroll">
        <div v-if="loading" class="loading-state">
          <i class="el-icon-loading"></i>
          <span>正在同步消息流</span>
        </div>

        <div v-else-if="loadError && !events.length" class="empty-state is-error">
          <i class="el-icon-warning-outline"></i>
          <h2>暂时无法读取消息</h2>
          <p>{{ loadError }}</p>
          <el-button @click="loadEvents(true)">重新加载</el-button>
        </div>

        <div v-else-if="!events.length" class="empty-state">
          <i class="el-icon-bell"></i>
          <h2>{{ hasActiveFilters ? '没有符合筛选条件的消息' : '消息流还是空的' }}</h2>
          <p>{{ hasActiveFilters ? '可以减少筛选条件，或等待新的实时消息。' : '生产者完成双写后，新消息会自动出现在这里。' }}</p>
        </div>

        <div v-else class="event-list">
          <article
            v-for="event in events"
            :key="event.event_id"
            class="event-row"
            :class="[`severity-${event.severity}`, { 'is-expanded': isExpanded(event.event_id) }]"
            tabindex="0"
            :aria-expanded="isExpanded(event.event_id) ? 'true' : 'false'"
            @click="toggleExpanded(event.event_id)"
            @keydown.enter="toggleExpanded(event.event_id)"
            @keydown.space.prevent="toggleExpanded(event.event_id)"
          >
            <div class="event-time">
              <time :datetime="eventDatetime(event)">{{ formatTime(event.received_at) }}</time>
              <span>{{ formatDate(event.received_at) }}</span>
            </div>
            <div class="severity-rail"><i></i></div>
            <div class="event-main">
              <div class="event-meta">
                <span class="room-code">{{ event.room_code }}</span>
                <span class="severity-label">{{ severityLabel(event.severity) }}</span>
                <span v-if="event.recipient_type === 'person'" class="recipient-label">个人</span>
              </div>
              <div class="event-content">{{ event.content }}</div>
              <div class="event-foot">
                <span class="delivery-state"><i></i>面板已接收</span>
                <span class="expand-hint">{{ isExpanded(event.event_id) ? '收起' : '展开' }}</span>
              </div>
            </div>
          </article>

          <div class="flow-footer">
            <el-button v-if="hasMore" :loading="loadingMore" @click="loadMore">加载更早消息</el-button>
            <span v-else>已到达当前消息流的起点</span>
          </div>
        </div>
      </div>
    </template>
  </section>
</template>

<script>
import dayjs from 'dayjs'
import {
  createNotificationSocket,
  getNotificationEvents,
  getNotificationFilters
} from '../../api/notification'
import {
  getNotificationToken,
  removeNotificationToken,
  removeUserId,
  removeUserName
} from '../../utils/store'
import {
  getNotificationTokenState,
  isNotificationTokenAuthError,
  notificationTokenRefreshDelay,
  renewNotificationToken
} from '../../utils/notificationToken'

const TOKEN_REFRESH_RETRY_MIN_MS = 30000
const TOKEN_REFRESH_RETRY_MAX_MS = 5 * 60 * 1000

export default {
  name: 'NotificationCenter',
  data () {
    return {
      token: '',
      authRequired: false,
      authMessage: '登录状态已失效。重新登录后即可恢复历史查询和实时推送。',
      events: [],
      nextCursor: null,
      hasMore: false,
      loading: false,
      loadingMore: false,
      loadError: '',
      socket: null,
      socketState: 'connecting',
      reconnectAttempt: 0,
      reconnectTimer: null,
      refreshTimer: null,
      tokenRefreshTimer: null,
      tokenRefreshPromise: null,
      tokenRefreshRetryAttempt: 0,
      socketAuthRefreshAttempted: false,
      destroyed: false,
      isFullscreen: false,
      newEventCount: 0,
      expanded: {},
      filters: {
        keyword: '',
        room: '',
        severity: ''
      },
      filterOptions: {
        rooms: []
      }
    }
  },
  computed: {
    socketStateLabel () {
      return {
        connecting: '连接中',
        connected: '实时连接',
        reconnecting: '正在重连',
        offline: '连接已断开'
      }[this.socketState]
    },
    hasActiveFilters () {
      return Boolean(this.filters.keyword || this.filters.room || this.filters.severity)
    }
  },
  async mounted () {
    document.addEventListener('fullscreenchange', this.syncFullscreenState)
    document.addEventListener('webkitfullscreenchange', this.syncFullscreenState)
    document.addEventListener('visibilitychange', this.handleTokenRefreshWake)
    window.addEventListener('focus', this.handleTokenRefreshWake)
    this.syncFullscreenState()
    this.token = getNotificationToken() || ''
    if (!this.token) {
      this.authRequired = true
      this.socketState = 'offline'
      return
    }
    try {
      await this.ensureFreshNotificationToken()
    } catch (error) {
      if (isNotificationTokenAuthError(error)) {
        this.requireAuthentication(error.message)
        return
      }
      this.scheduleTokenRefreshRetry()
    }
    await Promise.all([this.loadFilters(), this.loadEvents(true)])
    if (!this.authRequired) this.connectSocket()
  },
  beforeDestroy () {
    this.destroyed = true
    clearTimeout(this.reconnectTimer)
    clearTimeout(this.refreshTimer)
    clearTimeout(this.tokenRefreshTimer)
    document.removeEventListener('fullscreenchange', this.syncFullscreenState)
    document.removeEventListener('webkitfullscreenchange', this.syncFullscreenState)
    document.removeEventListener('visibilitychange', this.handleTokenRefreshWake)
    window.removeEventListener('focus', this.handleTokenRefreshWake)
    if (this.socket) this.socket.close()
  },
  methods: {
    ensureFreshNotificationToken (options = {}) {
      if (this.tokenRefreshPromise) return this.tokenRefreshPromise
      const refreshPromise = renewNotificationToken(options)
        .then((result) => {
          this.token = result.token
          this.tokenRefreshRetryAttempt = 0
          this.scheduleTokenRefresh(result.token)
          return result
        })
        .finally(() => {
          if (this.tokenRefreshPromise === refreshPromise) this.tokenRefreshPromise = null
        })
      this.tokenRefreshPromise = refreshPromise
      return refreshPromise
    },
    scheduleTokenRefresh (token = this.token) {
      clearTimeout(this.tokenRefreshTimer)
      if (this.destroyed) return
      const delay = notificationTokenRefreshDelay(token)
      if (delay === null) {
        this.requireAuthentication('登录令牌已失效，请重新登录。')
        return
      }
      this.tokenRefreshTimer = setTimeout(
        () => this.refreshNotificationToken(),
        Math.max(1000, delay)
      )
    },
    scheduleTokenRefreshRetry () {
      clearTimeout(this.tokenRefreshTimer)
      if (this.destroyed) return
      const state = getNotificationTokenState(this.token)
      if (!state.valid) {
        this.requireAuthentication('登录令牌已失效，请重新登录。')
        return
      }
      this.tokenRefreshRetryAttempt += 1
      const retryDelay = Math.min(
        TOKEN_REFRESH_RETRY_MAX_MS,
        TOKEN_REFRESH_RETRY_MIN_MS * Math.pow(2, Math.min(this.tokenRefreshRetryAttempt - 1, 3))
      )
      const beforeExpiryDelay = Math.max(1000, (state.remainingSeconds - 5) * 1000)
      this.tokenRefreshTimer = setTimeout(
        () => this.refreshNotificationToken(),
        Math.min(retryDelay, beforeExpiryDelay)
      )
    },
    async refreshNotificationToken (options = {}) {
      try {
        return await this.ensureFreshNotificationToken(options)
      } catch (error) {
        if (isNotificationTokenAuthError(error)) {
          this.requireAuthentication(error.message)
        } else {
          this.scheduleTokenRefreshRetry()
        }
        return null
      }
    },
    handleTokenRefreshWake () {
      if (document.hidden || this.destroyed || this.authRequired) return
      this.refreshNotificationToken()
    },
    requireAuthentication (message) {
      clearTimeout(this.reconnectTimer)
      clearTimeout(this.tokenRefreshTimer)
      this.authRequired = true
      this.authMessage = message || '登录状态已失效。重新登录后即可恢复历史查询和实时推送。'
      this.socketState = 'offline'
      removeNotificationToken()
      if (this.socket) {
        const socket = this.socket
        this.socket = null
        socket.close()
      }
    },
    async handleSocketAuthenticationFailure () {
      const tokenState = getNotificationTokenState(this.token)
      if (tokenState.valid && !this.socketAuthRefreshAttempted) {
        this.socketAuthRefreshAttempted = true
        try {
          await this.ensureFreshNotificationToken({ force: true })
          if (!this.destroyed && !this.authRequired) this.connectSocket()
        } catch (error) {
          const message = isNotificationTokenAuthError(error)
            ? error.message
            : '实时认证失败，自动续期暂时不可用，请重新登录。'
          this.requireAuthentication(message)
        }
        return
      }
      this.requireAuthentication('实时连接认证失败，请重新登录。')
    },
    fullscreenElement () {
      return document.fullscreenElement || document.webkitFullscreenElement || null
    },
    syncFullscreenState () {
      this.isFullscreen = Boolean(this.fullscreenElement())
    },
    async toggleFullscreen () {
      try {
        if (this.fullscreenElement()) {
          const exit = document.exitFullscreen || document.webkitExitFullscreen
          if (exit) await exit.call(document)
          return
        }
        const request = this.$el.requestFullscreen || this.$el.webkitRequestFullscreen
        if (!request) throw new Error('Fullscreen API is unavailable')
        await request.call(this.$el)
      } catch (error) {
        this.$message.warning('当前浏览器未允许进入全屏')
      }
    },
    async leaveFullscreen () {
      if (!this.fullscreenElement()) return
      const exit = document.exitFullscreen || document.webkitExitFullscreen
      if (exit) await exit.call(document)
    },
    async goHome () {
      await this.leaveFullscreen()
      this.$router.push('/nav')
    },
    queryParams (cursor) {
      const params = { limit: 50 }
      if (this.filters.room) params.room = this.filters.room
      if (this.filters.severity) params.severity = this.filters.severity
      if (this.filters.keyword.trim()) params.q = this.filters.keyword.trim()
      if (cursor) params.cursor = cursor
      return params
    },
    async loadFilters () {
      try {
        const response = await getNotificationFilters(this.token)
        this.filterOptions = response.data.data || { rooms: [] }
      } catch (error) {
        this.handleRequestError(error)
      }
    },
    async loadEvents (reset) {
      if (reset) {
        this.loading = true
        this.nextCursor = null
        this.loadError = ''
      } else {
        this.loadingMore = true
      }
      try {
        const response = await getNotificationEvents(this.token, this.queryParams(reset ? null : this.nextCursor))
        const payload = response.data
        const incoming = Array.isArray(payload.data) ? payload.data : []
        this.events = reset ? incoming : this.uniqueEvents(this.events.concat(incoming))
        this.nextCursor = payload.meta ? payload.meta.next_cursor : null
        this.hasMore = Boolean(payload.meta && payload.meta.has_more)
      } catch (error) {
        this.loadError = this.handleRequestError(error)
      } finally {
        this.loading = false
        this.loadingMore = false
      }
    },
    loadMore () {
      if (!this.loadingMore && this.hasMore) this.loadEvents(false)
    },
    async refreshFlow () {
      clearTimeout(this.refreshTimer)
      this.newEventCount = 0
      await this.loadEvents(true)
      this.subscribeSocket()
    },
    queueRefresh () {
      clearTimeout(this.refreshTimer)
      this.refreshTimer = setTimeout(() => this.refreshFlow(), 350)
    },
    resetFilters () {
      this.filters = { keyword: '', room: '', severity: '' }
      this.refreshFlow()
    },
    uniqueEvents (events) {
      const seen = new Set()
      return events.filter((event) => {
        if (!event || seen.has(event.event_id)) return false
        seen.add(event.event_id)
        return true
      })
    },
    connectSocket () {
      if (this.destroyed || this.authRequired) return
      this.socketState = this.reconnectAttempt ? 'reconnecting' : 'connecting'
      const socket = createNotificationSocket()
      this.socket = socket
      socket.onopen = () => {
        socket.send(JSON.stringify({ type: 'authenticate', token: this.token }))
      }
      socket.onmessage = (message) => {
        let payload
        try {
          payload = JSON.parse(message.data)
        } catch (error) {
          return
        }
        if (payload.type === 'ready') {
          this.reconnectAttempt = 0
          this.socketAuthRefreshAttempted = false
          this.socketState = 'connected'
          this.scheduleTokenRefresh()
          this.subscribeSocket()
        } else if (payload.type === 'event' && payload.data) {
          this.receiveEvent(payload.data)
        }
      }
      socket.onerror = () => {
        this.socketState = 'offline'
      }
      socket.onclose = (event) => {
        if (this.socket === socket) this.socket = null
        this.socketState = 'offline'
        if (event.code === 4401) {
          this.handleSocketAuthenticationFailure()
          return
        }
        if (!this.destroyed && !this.authRequired) this.scheduleReconnect()
      }
    },
    subscribeSocket () {
      if (!this.socket || this.socket.readyState !== WebSocket.OPEN) return
      this.socket.send(JSON.stringify({
        type: 'subscribe',
        rooms: this.filters.room ? [this.filters.room] : [],
        sources: [],
        severities: this.filters.severity ? [this.filters.severity] : []
      }))
    },
    scheduleReconnect () {
      clearTimeout(this.reconnectTimer)
      this.reconnectAttempt += 1
      this.socketState = 'reconnecting'
      const delay = Math.min(30000, 1000 * Math.pow(2, Math.min(this.reconnectAttempt - 1, 5)))
      this.reconnectTimer = setTimeout(() => this.connectSocket(), delay)
    },
    receiveEvent (event) {
      if (!this.eventMatchesFilters(event)) return
      if (this.events.some((item) => item.event_id === event.event_id)) return
      const flow = this.$refs.flow
      const preservePosition = Boolean(flow && flow.scrollTop > 80)
      const previousHeight = preservePosition ? flow.scrollHeight : 0
      this.events.unshift(event)
      if (preservePosition) {
        this.newEventCount += 1
        this.$nextTick(() => {
          flow.scrollTop += flow.scrollHeight - previousHeight
        })
      } else {
        this.$nextTick(() => { if (flow) flow.scrollTop = 0 })
      }
      if (!this.filterOptions.rooms.includes(event.room_code)) this.filterOptions.rooms.push(event.room_code)
    },
    eventMatchesFilters (event) {
      if (this.filters.room && event.room_code !== this.filters.room) return false
      if (this.filters.severity && event.severity !== this.filters.severity) return false
      const keyword = this.filters.keyword.trim().toLowerCase()
      return !keyword || String(event.content).toLowerCase().includes(keyword)
    },
    handleScroll () {
      if (this.$refs.flow && this.$refs.flow.scrollTop < 40) this.newEventCount = 0
    },
    showNewest () {
      if (this.$refs.flow) this.$refs.flow.scrollTo({ top: 0, behavior: 'smooth' })
      this.newEventCount = 0
    },
    isExpanded (eventId) {
      return this.expanded[eventId] !== false
    },
    toggleExpanded (eventId) {
      this.$set(this.expanded, eventId, !this.isExpanded(eventId))
    },
    handleRequestError (error) {
      const status = error && error.response ? error.response.status : 0
      if (status === 401) {
        this.requireAuthentication('登录令牌已失效，请重新登录。')
        return '登录令牌已失效，请重新登录。'
      }
      return status ? `服务返回错误（${status}）` : '无法连接消息服务，请稍后重试。'
    },
    async reauthenticate () {
      await this.leaveFullscreen()
      removeNotificationToken()
      removeUserId()
      removeUserName()
      this.$router.replace('/login')
    },
    severityLabel (severity) {
      return { info: '普通', success: '成功', warning: '注意', error: '异常' }[severity] || '普通'
    },
    formatTime (timestamp) {
      return dayjs(timestamp).format('HH:mm:ss')
    },
    formatDate (timestamp) {
      const date = dayjs(timestamp)
      return date.isSame(dayjs(), 'day') ? '今天' : date.format('YYYY-MM-DD')
    },
    eventDatetime (event) {
      return dayjs(event.received_at).toISOString()
    }
  }
}
</script>

<style lang="scss" scoped>
.notification-center {
  --blue: #4a8af4;
  --ink: #24344b;
  --muted: #7d8ba0;
  --line: #e8edf5;
  position: relative;
  display: flex;
  flex-direction: column;
  width: 100%;
  height: 100vh;
  min-height: 560px;
  color: var(--ink);
  background: #f7f9fc;
  overflow: hidden;
}

.center-header {
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  padding: 24px 30px 18px;
  background: #ffffff;
  border-bottom: 1px solid var(--line);

  .eyebrow {
    margin-bottom: 6px;
    color: var(--blue);
    font-size: 11px;
    font-weight: 600;
    letter-spacing: 1.6px;
  }

  h1 {
    margin: 0;
    font-size: 26px;
    line-height: 34px;
    font-weight: 600;
  }

  p {
    margin: 7px 0 0;
    color: var(--muted);
    font-size: 13px;
  }
}

.title-line {
  display: flex;
  align-items: center;
  gap: 14px;
}

.connection-state {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  color: #7d8ba0;
  font-size: 12px;

  i {
    width: 7px;
    height: 7px;
    border-radius: 50%;
    background: #aab4c3;
  }

  &.is-connected { color: #27885b; }
  &.is-connected i { background: #35a76f; box-shadow: 0 0 0 3px #e2f5eb; }
  &.is-connecting i,
  &.is-reconnecting i { background: #e5a437; box-shadow: 0 0 0 3px #fbf0dd; }
}

.flow-summary {
  display: flex;
  align-items: baseline;
  gap: 5px;
  color: var(--muted);
  font-size: 12px;

  strong {
    color: var(--ink);
    font-size: 24px;
    font-weight: 600;
  }
}

.header-actions {
  display: flex;
  align-items: center;
  gap: 10px;

  .flow-summary { margin-right: 10px; }
}

.filter-bar {
  display: grid;
  grid-template-columns: minmax(240px, 1.7fr) repeat(2, minmax(130px, 0.8fr)) auto;
  gap: 12px;
  align-items: end;
  padding: 14px 30px 16px;
  background: #ffffff;
  border-bottom: 1px solid var(--line);
}

.filter-field {
  min-width: 0;

  label {
    display: block;
    margin-bottom: 6px;
    color: #75849a;
    font-size: 12px;
  }

  ::v-deep .el-select { width: 100%; }
  ::v-deep .el-input__inner { height: 36px; line-height: 36px; border-color: #dfe5ee; }
}

.reset-button { height: 36px; }

.new-event-strip {
  position: absolute;
  z-index: 4;
  top: 178px;
  left: 50%;
  display: flex;
  align-items: center;
  gap: 18px;
  padding: 9px 12px 9px 15px;
  transform: translateX(-50%);
  color: #2e65b9;
  background: #edf4ff;
  border: 1px solid #ccddf9;
  border-radius: 3px;
  box-shadow: 0 5px 14px rgba(38, 79, 140, 0.12);
  font-size: 12px;
  cursor: pointer;

  button {
    padding: 0;
    color: #2e65b9;
    background: transparent;
    border: 0;
    cursor: pointer;
  }
}

.flow {
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  padding: 0 30px 28px;
  scroll-behavior: smooth;
}

.event-list {
  width: 100%;
  max-width: 1680px;
  margin: 0 auto;
  background: #ffffff;
  border-right: 1px solid var(--line);
  border-left: 1px solid var(--line);
}

.event-row {
  display: grid;
  grid-template-columns: 92px 18px minmax(0, 1fr);
  min-height: 116px;
  padding: 0 22px 0 18px;
  background: #ffffff;
  border-bottom: 1px solid var(--line);
  outline: none;
  cursor: pointer;
  transition: background-color 160ms ease-out;

  &:hover,
  &:focus-visible { background: #f9fbff; }
}

.event-time {
  padding-top: 23px;
  text-align: right;

  time {
    display: block;
    color: #3e4c60;
    font-size: 13px;
    font-weight: 600;
    font-variant-numeric: tabular-nums;
  }

  span {
    display: block;
    margin-top: 4px;
    color: #9aa5b4;
    font-size: 11px;
  }
}

.severity-rail {
  position: relative;

  &::before {
    position: absolute;
    top: 0;
    bottom: 0;
    left: 50%;
    width: 1px;
    content: '';
    background: #e5eaf1;
  }

  i {
    position: absolute;
    z-index: 1;
    top: 26px;
    left: 50%;
    width: 8px;
    height: 8px;
    transform: translateX(-50%);
    background: #7e9dcc;
    border: 3px solid #ffffff;
    border-radius: 50%;
    box-shadow: 0 0 0 1px #b8c9e0;
  }
}

.severity-success .severity-rail i { background: #35a76f; box-shadow: 0 0 0 1px #89c9aa; }
.severity-warning .severity-rail i { background: #e5a437; box-shadow: 0 0 0 1px #e9c47f; }
.severity-error .severity-rail i { background: #d95555; box-shadow: 0 0 0 1px #e9a1a1; }

.event-main {
  min-width: 0;
  padding: 20px 0 15px 14px;
}

.event-meta {
  display: flex;
  align-items: center;
  gap: 8px;
  min-width: 0;
  margin-bottom: 9px;
  font-size: 11px;
}

.room-code {
  padding: 3px 7px;
  color: #2f67bc;
  background: #eaf2ff;
  border-radius: 3px;
  font-weight: 600;
}

.severity-label,
.recipient-label { color: #929ead; }
.severity-error .severity-label { color: #c54343; }
.severity-warning .severity-label { color: #a96c0f; }
.severity-success .severity-label { color: #27885b; }

.event-content {
  display: -webkit-box;
  overflow: hidden;
  color: #2d3b4f;
  font-size: 14px;
  line-height: 22px;
  white-space: pre-wrap;
  overflow-wrap: anywhere;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 2;
}

.event-row.is-expanded .event-content {
  display: block;
  overflow: visible;
}

.event-foot {
  display: flex;
  gap: 14px;
  margin-top: 10px;
  color: #a0a9b6;
  font-size: 11px;
}

.delivery-state {
  display: inline-flex;
  align-items: center;
  gap: 5px;

  i { width: 5px; height: 5px; background: #48aa78; border-radius: 50%; }
}

.expand-hint { margin-left: auto; color: #7792b8; }

.flow-footer {
  display: flex;
  justify-content: center;
  padding: 22px;
  color: #9aa5b4;
  font-size: 12px;
}

.loading-state,
.empty-state,
.auth-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 320px;
  color: #8a97a9;

  > i { margin-bottom: 14px; color: #6e99db; font-size: 32px; }
  h2 { margin: 0 0 8px; color: #3a495d; font-size: 17px; font-weight: 500; }
  p { max-width: 430px; margin: 0 0 20px; text-align: center; font-size: 13px; line-height: 21px; }
}

.auth-state {
  flex: 1;
  background: #ffffff;

  .auth-mark {
    display: grid;
    place-items: center;
    width: 54px;
    height: 54px;
    margin-bottom: 17px;
    color: #4a8af4;
    background: #eaf2ff;
    border-radius: 50%;
    font-size: 22px;
  }
}

.empty-state.is-error > i { color: #d15a5a; }

@media (max-width: 1050px) {
  .filter-bar { grid-template-columns: repeat(2, minmax(180px, 1fr)); }
  .reset-button { justify-self: start; }
}

@media (max-width: 720px) {
  .notification-center { height: 100vh; min-height: 0; }
  .center-header { align-items: flex-start; padding: 20px; }
  .flow-summary { display: none; }
  .header-actions { gap: 6px; }
  .header-actions ::v-deep .el-button span { display: none; }
  .header-actions ::v-deep .el-button { padding: 10px; }
  .filter-bar { grid-template-columns: 1fr; padding: 14px 20px; }
  .flow { padding: 0 12px 20px; }
  .event-row { grid-template-columns: 1fr; padding: 16px; }
  .event-time { display: flex; gap: 6px; padding: 0 0 9px; text-align: left; }
  .event-time time,
  .event-time span { display: inline; margin: 0; }
  .severity-rail { display: none; }
  .event-main { padding: 0; }
}

@media (prefers-reduced-motion: reduce) {
  .event-row { transition: none; }
  .flow { scroll-behavior: auto; }
}
</style>
